import { IMDSSwitchProps, IMDSSwitchVariant } from "./switch.types";
import styled from "@emotion/styled";
import { Switch } from "react-aria-components";
import { Theme } from "@emotion/react";

const getVariantColor = ({ theme, variant }: { theme: Theme; variant?: string }) => {
  switch (variant) {
    case IMDSSwitchVariant.WHITE:
      return theme.colors.text.onAccent;
    case IMDSSwitchVariant.BLACK:
      return theme.colors.text.contrast;
    case IMDSSwitchVariant.GREY:
      return theme.colors.grey.x400;
    case IMDSSwitchVariant.GREEN:
      return theme.colors.green.x500;
    case IMDSSwitchVariant.PURPLE:
      return theme.colors.lilac.x500;
    case IMDSSwitchVariant.TOGGLE:
      return theme.colors.transparent;
    default:
      return theme.colors.grey.x0;
  }
};

export const StyledSwitch = styled(Switch)<Pick<IMDSSwitchProps, "variant">>`
  display: flex;
  align-items: center;
  gap: 0.571rem;
  font-size: 1.143rem;
  color: ${getVariantColor};
  forced-color-adjust: none;
  cursor: pointer;

  .indicator {
    width: 2rem;
    height: 1.143rem;
    border: 2px solid ${getVariantColor};
    background: transparent;
    ${({ variant, theme }) =>
      variant === IMDSSwitchVariant.TOGGLE &&
      `
        border: 2px solid ${theme.colors.icon.placeholder};
        background: ${theme.colors.icon.placeholder};
      `}
    border-radius: 1.143rem;
    transition: all 200ms;

    &:before {
      content: "";
      display: block;
      margin: 1px;
      width: 0.75rem;
      height: 0.75rem;
      background: ${getVariantColor};
      ${({ variant, theme }) =>
        variant === IMDSSwitchVariant.TOGGLE &&
        `
          width: 0.85rem;
          height: 0.85rem;
          background: ${theme.colors.icon.onAccent};
          transform: translateY(-5%);
        `}
      border-radius: 16px;
      transition: all 200ms;
    }
  }

  &[data-pressed] .indicator {
    border-color: ${getVariantColor};

    &:before {
      background: ${getVariantColor};
    }
  }

  &[data-selected] {
    .indicator {
      border-color: ${getVariantColor};
      background: transparent;
      ${({ variant, theme }) =>
        variant === IMDSSwitchVariant.TOGGLE &&
        `
          border-color: ${theme.colors.icon.selected};
          background: ${theme.colors.icon.selected};
        `};

      &:before {
        background: ${getVariantColor};
        transform: translateX(100%);
        ${({ variant, theme }) =>
          variant === IMDSSwitchVariant.TOGGLE &&
          `
            background: ${theme.colors.icon.onAccent};
            transform: translateY(-5%) translateX(92%);
          `};
      }
    }

    &[data-pressed] {
      .indicator {
        border-color: ${getVariantColor};
        background: transparent;
      }
    }
  }

  &[data-focus-visible] .indicator {
    outline: 2px solid ${({ theme }) => theme.colors.blue.x400};
    outline-offset: 2px;
  }

  &[data-disabled] {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.grey.x200};

    .indicator {
      border-color: ${({ theme }) => theme.colors.grey.x200};
      background-color: ${({ theme }) => theme.colors.grey.x0};

      &:before {
        background: ${({ theme }) => theme.colors.grey.x200};
      }
    }
  }
`;
